import TagManager from 'react-gtm-module';
import { isExpired } from 'react-jwt';
import { NavigateFunction } from 'react-router-dom';

import { storeMfaSetupContext, storeMfaTotpContext } from 'utils/mfaSessionStorage';
import { persistSessionTokens } from 'utils/sessionTokens';

export interface LoginSuccessPayload {
  token: string;
  expiry?: string;
  user?: { id?: number | string };
  refresh_token?: string;
  refresh_token_expiry?: string;
}

export function persistLoginTokens(payload: LoginSuccessPayload) {
  persistSessionTokens(payload);
}

export function completeLoginSession(options: {
  navigate: NavigateFunction;
  onAuthSuccess: (token: string, expiry: string) => void;
  payload: LoginSuccessPayload;
  /** When set, fires the same GTM login event as the main sign-in page. */
  gtmUserId?: number | string;
  showWelcomeScreen?: boolean;
}) {
  const { navigate, onAuthSuccess, payload, gtmUserId, showWelcomeScreen } = options;

  persistLoginTokens(payload);
  onAuthSuccess(payload.token, payload.expiry || '');

  if (gtmUserId != null) {
    TagManager.initialize({
      gtmId: 'GTM-NP7FL5N',
      events: {
        event: 'login',
        userId: gtmUserId,
      },
    });
  }

  const modelToken = window.localStorage.getItem('model-token');
  if (modelToken) {
    if (isExpired(modelToken)) {
      navigate('/home');
    } else {
      navigate(`/model/${modelToken}`);
    }
    localStorage.removeItem('model-token');
  } else if (showWelcomeScreen) {
    navigate('/welcome');
  } else {
    navigate('/home');
  }
}

/** Login response shape when MFA is required (fields depend on mfa_type). */
export interface LoginMfaChallengeResponse {
  mfa_required: true;
  mfa_type: string;
  setup_token?: string;
  cognito_access_token?: string;
  session?: string;
  user_id?: string | number;
  username?: string;
}

export function isLoginMfaResponse(data: any): data is LoginMfaChallengeResponse {
  return Boolean(data?.mfa_required && data?.mfa_type);
}

/** MFA challenge or authenticated session — shared by sign-in and set-new-password. */
export type LoginAuthHandleResult = 'mfa' | 'session' | 'none';

export function handleLoginAuthPayload(options: {
  navigate: NavigateFunction;
  onAuthSuccess: (token: string, expiry: string) => void;
  payload: any;
  /** Fallback email when payload.username is absent (e.g. sign-in form value). */
  email: string;
  gtmUserId?: number | string;
  showWelcomeScreen?: boolean;
}): LoginAuthHandleResult {
  const { navigate, onAuthSuccess, payload, email, gtmUserId, showWelcomeScreen } = options;
  if (!payload) {
    return 'none';
  }

  const mfaEmail = payload.username || email;

  if (isLoginMfaResponse(payload)) {
    if (
      payload.mfa_type === 'SETUP_REQUIRED' &&
      payload.setup_token &&
      payload.cognito_access_token
    ) {
      storeMfaSetupContext(payload.setup_token, payload.cognito_access_token, mfaEmail);
      navigate('/mfa/setup');
      return 'mfa';
    }
    if (payload.mfa_type === 'TOTP' && payload.session != null) {
      storeMfaTotpContext(payload.session, payload.user_id ?? '', mfaEmail);
      navigate('/mfa/totp');
      return 'mfa';
    }
  }

  if (payload.token) {
    completeLoginSession({
      navigate,
      onAuthSuccess,
      payload: {
        token: payload.token,
        expiry: payload.expiry,
        user: payload.user,
        refresh_token: payload.refresh_token,
        refresh_token_expiry: payload.refresh_token_expiry,
      },
      gtmUserId: gtmUserId ?? payload.user?.id,
      showWelcomeScreen,
    });
    return 'session';
  }

  return 'none';
}
